import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FileText, Eye, EyeOff, ArrowRight, ShieldCheck, RefreshCw, ArrowLeft } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const { login, verify2FA, resend2FA, cancel2FA, pending2FA } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resending, setResending] = useState(false);
  const codeRefs = useRef([]);

  useEffect(() => {
    if (pending2FA && codeRefs.current[0]) {
      codeRefs.current[0].focus();
    }
  }, [pending2FA]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result?.requires_2fa) {
        toast.success('Verification code sent to your email');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid email or password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pasted[i] || '';
      }
      setCode(newCode);
      const focusIdx = Math.min(pasted.length, 5);
      codeRefs.current[focusIdx]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      toast.error('Please enter the full 6-digit code');
      return;
    }
    setLoading(true);
    try {
      await verify2FA(fullCode);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid verification code';
      toast.error(message);
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resend2FA();
      toast.success('New code sent to your email');
      setCode(['', '', '', '', '', '']);
      codeRefs.current[0]?.focus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    cancel2FA();
    setCode(['', '', '', '', '', '']);
  };

  if (pending2FA) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center" data-testid="verify-2fa-page">
        <div className="w-full max-w-md px-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
          </div>
          <Card className="bg-white border-stone-200 shadow-card">
            <CardHeader className="text-center">
              <CardTitle className="font-heading text-2xl text-primary-900">Verify Your Identity</CardTitle>
              <CardDescription className="text-stone-600">
                We sent a 6-digit code to <span className="font-medium text-stone-800">{pending2FA.email_hint}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVerify} className="space-y-6">
                <div className="flex justify-center gap-2" onPaste={handleCodePaste}>
                  {code.map((digit, i) => (
                    <Input
                      key={i}
                      ref={el => codeRefs.current[i] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(i, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(i, e)}
                      className="w-12 h-14 text-center text-xl font-bold bg-white border-stone-300 focus:border-primary focus:ring-primary"
                      data-testid={`2fa-code-input-${i}`}
                    />
                  ))}
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-primary hover:bg-primary-800 text-white"
                  disabled={loading || code.join('').length !== 6}
                  data-testid="verify-2fa-submit"
                >
                  {loading ? <div className="w-5 h-5 spinner" /> : 'Verify & Sign In'}
                </Button>
              </form>
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={handleBackToLogin}
                  className="text-sm text-stone-500 hover:text-stone-700 flex items-center gap-1"
                  data-testid="back-to-login-btn"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to login
                </button>
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="text-sm text-primary hover:underline flex items-center gap-1 disabled:opacity-50"
                  data-testid="resend-code-btn"
                >
                  <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} /> Resend code
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center bg-[#FAFAFA]" data-testid="login-page">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-[#11302A] to-emerald-600 rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-2xl text-[#0A0A0A]">RaiseRoot</span>
          </Link>
        </div>

        <Card className="bg-white border-[#E2E8E6] shadow-lg rounded-2xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-heading text-2xl text-[#0A0A0A]">Welcome back</CardTitle>
            <CardDescription className="text-[#5C6B67]">
              Log in to continue building your funding pipeline
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#0A0A0A] text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@nonprofit.org"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-11 bg-[#FAFAFA] border-[#E2E8E6] focus:border-[#00A859] rounded-xl"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[#0A0A0A] text-sm font-medium">Password</Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-[#00A859] hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="h-11 bg-[#FAFAFA] border-[#E2E8E6] focus:border-[#00A859] pr-10 rounded-xl"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#11302A] hover:bg-[#0D241F] text-white rounded-full font-medium text-base"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <div className="w-5 h-5 spinner" />
                ) : (
                  <>
                    Log in
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-[#5C6B67]">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#00A859] hover:underline font-medium">
                  Start free
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
